<template>
  <el-form-item
    class="country-selector"
    :class="{ desktop: isDesktop }"
    label-position="top"
  >
    <el-radio v-model="worldwide" :value="true"> All countries </el-radio>
    <el-radio v-model="worldwide" :value="false"> Specific countries </el-radio>
    <div v-if="!worldwide" class="tier-selector">
      <el-link
        v-for="tier in TierLabels"
        :key="tier.id"
        type="primary"
        class="tier-label"
        @click="selectTier(tier)"
      >
        {{ tier.label }}
      </el-link>
    </div>
    <el-transfer
      v-if="!worldwide"
      v-model="value"
      label-position="top"
      :filterable="true"
      :titles="['All countries', 'Target countries']"
      :left-default-checked="originalCountries"
      :data="countries"
      @update:modelValue="update"
    />
  </el-form-item>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { ICountry } from '@/api/countries'
import { TierRange, TierLabel, CountryEntry } from '@/api/types'

import { useCountriesStore } from '@/store/modules/countries'
import useResize from '@/hooks/useResize'

const TierLabels: Array<TierLabel> = [
  {
    id: TierRange.Tier1,
    label: 'Tier 1'
  },
  {
    id: TierRange.Tier2,
    label: 'Tier 2'
  },
  {
    id: TierRange.Tier3,
    label: 'Tier 3'
  },
  {
    id: TierRange.None,
    label: 'None'
  }
]
export default defineComponent({
  name: 'CountrySelector',
  props: {
    modelValue: {
      type: Array,
      required: true
    }
  },
  setup(props, context) {
    const countriesStore = useCountriesStore()
    countriesStore.GetItems()

    const value = ref(props.modelValue)

    const originalCountries = ref()
    originalCountries.value = [...props.modelValue]

    const worldwide = ref(props.modelValue.length === 0)
    const countries = computed(() => {
      const countries = countriesStore.sorted
      return countries
        .filter((country: ICountry) => country.attributes.iso !== 'un')
        .map<CountryEntry>((country: ICountry) => {
          return {
            key: country.attributes.iso,
            label: country.attributes.name,
            disabled: false,
            tier: country.attributes.tier
          }
        })
    })

    const { isDesktop } = useResize()

    watch(worldwide, (value, oldValue) => {
      if (value === true) {
        context.emit('update:modelValue', [])
      }
    })

    watch(
      (_: any) => props.modelValue,
      (val: any) => {
        worldwide.value = !val.length
      }
    )

    function update(newValue: any) {
      context.emit('update:modelValue', newValue)
    }

    const selectTier = (tier: TierLabel) => {
      const tierCountries = countries.value
        .filter((c: CountryEntry) => {
          return c.tier === tier.id
        })
        .map((c: CountryEntry) => c.key)
      originalCountries.value =
        tierCountries.length > 0
          ? [...originalCountries.value, ...tierCountries]
          : []
    }

    return {
      worldwide,
      countries,
      update,
      originalCountries,
      isDesktop,
      TierLabels,
      selectTier,
      value
    }
  }
})
</script>

<style lang="scss" scoped>
.el-transfer {
  margin-top: 1em;
}

.el-transfer :deep(.el-transfer-panel) {
  width: 100%;
}

.desktop .el-transfer :deep(.el-transfer-panel) {
  width: 250px;
}

.country-selector :deep(.el-transfer .el-input) {
  width: 100% !important;
  max-width: 100% !important;
}

.el-transfer :deep(.el-transfer-panel__item) {
  display: block;
}

.desktop .el-transfer :deep(.el-transfer__buttons) {
  width: auto;
}

.desktop .el-transfer :deep(.el-checkbox__label) {
  font-size: 10px;
}

.el-transfer :deep(.el-transfer__buttons) {
  width: 100%;
  text-align: center;
  margin: 1em 0;

  button {
    margin-bottom: 0;
  }
}

.tier-label {
  margin-right: 5px;
}
.tier-selector {
  width: 100%;
}
</style>
